import {Box, Drawer, List, ListItemButton, ListItemText, Toolbar} from '@mui/material';
import {ManualHeader} from './ManualHeader.tsx';
import {OnThisPage} from './OnThisPage.tsx';
import {PropsWithChildren, useRef} from 'react';
import {getPages} from '@area-monitoring/am-app-manual';
import {useNavigate, useParams} from '@tanstack/react-router';
import {useTranslation} from 'react-i18next';

const SIDEBAR_WIDTH = 300;

/** Strip a leading "N. " ordinal from a chapter title for display (order is preserved by sidebar_position). */
const stripLeadingNumber = (title: string): string => title.replace(/^\s*\d+[.)]\s*/, '');

export const ManualLayout: React.FC<PropsWithChildren> = ({children}) => {
  const {i18n} = useTranslation();
  const navigate = useNavigate();
  const params = useParams({strict: false}) as {slug?: string};
  const pages = getPages(i18n.language);
  const articleRef = useRef<HTMLDivElement>(null);

  return (
    <Box sx={{display: 'flex', height: '100%'}}>
      <ManualHeader />

      <Drawer
        variant="permanent"
        sx={{
          width: SIDEBAR_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: SIDEBAR_WIDTH,
            boxSizing: 'border-box',
            borderRight: 1,
            borderColor: 'divider',
          },
        }}
      >
        <Toolbar variant="dense" />
        <Box sx={{overflow: 'auto'}}>
          <List dense>
            {pages.map(page => (
              <ListItemButton
                key={page.slug}
                selected={params.slug === page.slug}
                onClick={() => void navigate({to: '/$slug', params: {slug: page.slug}})}
              >
                <ListItemText primary={stripLeadingNumber(page.frontmatter.title)} />
              </ListItemButton>
            ))}
          </List>
        </Box>
      </Drawer>

      <Box component="main" sx={{flexGrow: 1, height: '100%', overflow: 'auto'}}>
        <Toolbar variant="dense" />
        <Box sx={{display: 'flex', alignItems: 'flex-start', gap: 2, px: {xs: 0, md: 2}}}>
          <Box ref={articleRef} sx={{flexGrow: 1, minWidth: 0}}>
            {children}
          </Box>
          {params.slug && <OnThisPage containerRef={articleRef} slug={params.slug} />}
        </Box>
      </Box>
    </Box>
  );
};
